import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONSTANTS } from 'src/app/app.constants';
import { WellService } from './well.service';
import { Well } from '../interfaces/well.interface';
import { applyApiToGui } from '../scheme/scheme.utils';

@Injectable()
export class WellServiceKenLive extends WellService {
  // True once a GET_ALL_WELLS request is in flight or has already resolved. The well list is a
  // single, shared source read by both the map and list views — several components independently
  // call subscribeToWellList() on their own ngOnInit, and without this guard each one fires its
  // own request, widening the window in which a live connection-status push can race the list
  // load (see updateConnectionStatusInWellList) and momentarily surface a duplicate row/marker.
  private wellListRequested = false;
  // True once CONNECTION_ESTABLISHED has fired at least once. Distinguishes the very first
  // connect (where a component's ngOnInit is already about to call subscribeToWellList — a
  // forced refetch here would just be a second, redundant GET_ALL_WELLS request) from a genuine
  // reconnect after a dropped socket (where a refetch is actually needed).
  private hasConnectedOnce = false;

  /**
   * Subscribes to WebSocket events for well data and updates signals accordingly.
   */
  public constructor() {
    super();

    // Handle single well fetch. A reply for a well other than the open one answers a request made
    // before the user moved on (setCurrentWellById sets the new id synchronously), so it is
    // dropped without counting as the reply the open well's fetch is waiting for — otherwise it
    // would briefly show, and a modification parked for it would re-request, the well just left.
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_WELL, (msg) => {
      const recordId = msg.body?.['record']?.['id'];
      if (recordId && recordId !== this._signals.currentWell()?.id) return;
      if (msg.body) {
        const raw = msg.body['record'] as Well;
        const well = applyApiToGui(raw) as Well;
        this.setPosition({
          lat: well.location?.latitude ?? this.position().lat,
          lng: well.location?.longitude ?? this.position().lng,
        });
        this._signals.currentWell.set(well);
      }
      this.onWellReply();
    });

    // Handle all wells fetch
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_ALL_WELLS, (msg) => {
      if (!msg.body) return;
      this._signals.wells.set(this.dedupeWellsById(this.processWells(msg.body['records'] ?? [])));
    });

    // Refresh well list after add/edit
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.SET_WELL, () => {
      this.ws.sendWsRequest({ name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ALL_WELLS }, {});
    });

    // Refresh well list after delete
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.DELETE_WELL, () => {
      this.ws.sendWsRequest({ name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ALL_WELLS }, {});
    });

    // Update live users count
    this.ws.subscribeWs(APP_CONSTANTS.WELL_EVENTS.GET_LIVE_USERS, (msg) => {
      if (!msg.body) return;
      this._signals.liveUsers.set(msg.body['userIds'].length);
    });

    // Subscribe to connection status for all wells on every (re)connection so
    // the signal stays populated regardless of which view is active.
    this.ws.subscribeWs(APP_CONSTANTS.SESSION_EVENTS.CONNECTION_ESTABLISHED, () => {
      this.subscribeToWellEvent(null, APP_CONSTANTS.WELL_EVENTS.GET_CONNECTION_STATUS);
      // Only force a re-fetch on a genuine reconnect. CONNECTION_ESTABLISHED also fires on the
      // very first connect, where a component's ngOnInit is already about to call
      // subscribeToWellList() — resetting the guard here unconditionally caused a second,
      // redundant GET_ALL_WELLS request on every load (the "2 REQ/2 RES" symptom persisted
      // through the first version of this guard because of exactly this).
      if (this.hasConnectedOnce) {
        this.wellListRequested = false;
        this.subscribeToWellList();
      }
      this.hasConnectedOnce = true;
    });
  }

  /**
   * Requests the full list of wells from the server, once per connection lifetime.
   * Multiple views (map, list, search) each call this on init; only the first actually
   * sends a request — the rest just read the shared `wells` signal once it resolves.
   * Results are received via GET_ALL_WELLS event.
   */
  public subscribeToWellList(): void {
    if (this.wellListRequested) return;
    this.wellListRequested = true;
    this.ws.sendWsRequest({ name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ALL_WELLS }, {});
  }

  /**
   * Fetches a specific well by id and activates the real-time wire subscription for it.
   * When called without an id (navigating away), all signals are reset to null.
   * @param id Well id to fetch, or undefined to clear selection
   */
  public setCurrentWellById(id?: string): void {
    if (id) {
      // Clear cached WS messages when switching to a different well so that tab components
      // do not briefly display stale state from the previous well.
      const currentId = this._signals.currentWell()?.id;
      if (currentId !== id) {
        this.ws.clearMessageCache();
        this._signals.currentWell.set({ id } as Well);
      }
      this.requestWell(id);
      // Subscribe to live-users BEFORE SET_WELL_WIRES so the push triggered by the
      // server when it processes SET_WELL_WIRES is not missed on a cold page refresh.
      this.subscribeToWellEvent(id, APP_CONSTANTS.WELL_EVENTS.GET_LIVE_USERS);
      // Receive well config updates pushed by the server when any client saves this well.
      this.subscribeToWellEvent(id, APP_CONSTANTS.WELL_EVENTS.WELL_MODIFICATION);
      this.ws.sendWsRequest(
        { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_WELL_WIRES },
        { itemId: id, on: true },
      );
    } else {
      // Announce departure so the server removes this user from the active list
      // before clearing the signal (we need the id while it is still set).
      const leavingId = this._signals.currentWell()?.id;
      if (leavingId) {
        this.ws.sendWsRequest(
          { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_WELL_WIRES },
          { itemId: leavingId, on: false },
        );
      }
      this.ws.clearMessageCache();
      const { wells, liveUsers, ...currentWellSignals } = this._signals;
      Object.values(currentWellSignals).forEach((signal) => signal.set(null));
      this._signals.liveUsers.set(0);
    }
  }

  /**
   * Adds a new well.
   * @param data Well data to add
   * @returns Observable<{ success, id }> indicating save status and the new well's id
   */
  public addWell(data: any): Observable<{ success: boolean; id?: string }> {
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_WELL },
      { update: { $set: { ...data } } },
    );
    return this.watchSaveWellResult();
  }

  /**
   * Sends a SET_WELL request with a $set update payload containing only the changed fields.
   * @param sendData Changed fields to update
   */
  public saveWellInfo(sendData: any): Observable<boolean> {
    const id = this.currentWell()?.id;
    this.ws.sendWsRequest(
      { name: APP_CONSTANTS.REQUEST_MESSAGES.SET_WELL, feedbackId: null },
      {
        filter: { id: { $eq: id } },
        update: { $set: { ...sendData } },
      },
    );
    return this.watchSaveWell();
  }

  /**
   * Merges the incoming well list with any existing connectionStatus data so that
   * live status updates received before the list don't get overwritten with UNKNOWN.
   * @param wells Incoming well list
   * @returns Well[] with merged connectionStatus
   */
  private processWells(wells: any[]): Well[] {
    const currentWells = this._signals.wells();
    return wells.map((well) => {
      const existingWell = currentWells?.find((w) => w.id === well.id);
      return {
        ...well,
        connectionStatus: existingWell?.connectionStatus ?? {
          id: APP_CONSTANTS.CONNECTION_STATUSES.UNKNOWN.id,
          name: APP_CONSTANTS.CONNECTION_STATUSES.UNKNOWN.name,
        },
      };
    });
  }
}
